import React from "react";
import { cn } from "@/lib/utils";

interface CarouselIndicatorProps {
  totalSlides: number;
  currentSlide: number;
  onDotClick: (index: number) => void;
  maxDots?: number;
  className?: string;
  getAriaLabel?: (slideNum: number) => string;
}

export const CarouselIndicator = ({
  totalSlides,
  currentSlide,
  onDotClick,
  maxDots = 7,
  className,
  getAriaLabel = (slideNum: number) => `Go to slide ${slideNum}`,
}: CarouselIndicatorProps): React.ReactElement => {
  const getDotSize = (index: number, currentSlide: number): string => {
    const distance = Math.abs(index - currentSlide);

    if (distance === 0) {
      return "h-[10px] w-[10px]";
    } else if (distance <= 1) {
      return "h-2 w-2";
    } else if (distance === 2) {
      return "h-[6px] w-[6px]";
    } else {
      return "h-1 w-1";
    }
  };

  const getDotOpacity = (
    index: number,
    currentSlide: number,
    totalSlides: number,
  ): string => {
    if (totalSlides <= maxDots) {
      return "opacity-100";
    }

    const distance = Math.abs(index - currentSlide);
    const halfWindow = Math.floor(maxDots / 2);

    let shouldBeVisible = false;

    if (currentSlide < halfWindow) {
      shouldBeVisible = index < maxDots;
    } else if (currentSlide >= totalSlides - halfWindow) {
      shouldBeVisible = index >= totalSlides - maxDots;
    } else {
      shouldBeVisible =
        index >= currentSlide - halfWindow &&
        index <= currentSlide + halfWindow;
    }

    if (!shouldBeVisible) return "opacity-0";

    if (distance === 0) return "opacity-100";
    if (distance === 1) return "opacity-80";
    if (distance === 2) return "opacity-60";
    return "opacity-40";
  };

  if (totalSlides <= 1) {
    return <></>;
  }

  return (
    <div className={cn("mt-4 flex justify-center gap-2", className)}>
      {Array.from({ length: totalSlides }).map((_, index) => (
        <button
          key={`dot-${index}`}
          className={cn(
            "rounded-full transition-all duration-300 ease-out",
            getDotSize(index, currentSlide),
            getDotOpacity(index, currentSlide, totalSlides),
            index === currentSlide ? "bg-foreground" : "bg-muted-foreground/30",
          )}
          onClick={() => onDotClick(index)}
          aria-label={getAriaLabel(index + 1)}
        />
      ))}
    </div>
  );
};
